<template>
  <div class="w-full">
    <!-- Input -->
    <input v-if="type === 'input'" :required="required" :id="id" :type="inputType" v-model="localValue"
      :placeholder="placeholder"
      class="block w-full rounded-md border border-gray-300 px-3 py-3 shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      :class="{ 'border-red-500': error || showRequiredError }" :disabled="disabled" :name="name" :min="min" :max="max"
      @blur="onBlur" />

    <!-- Select -->
    <select v-else-if="type === 'select'" :required="required" :disabled="disabled" :id="id" :name="name"
      v-model="localValue"
      class="block w-full rounded-md border border-gray-300 px-3 py-3 shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      :class="{ 'border-red-500': error || showRequiredError }" @blur="onBlur">
      <option disabled value="">
        {{ placeholder || translate("Select an option") }}
      </option>
      <option v-for="option in options" :key="option.value" :value="option.value">
        {{ option.label }}
      </option>
    </select>

    <!-- Toggle -->
    <div v-else-if="type === 'toggle'" class="flex items-center">
      <button @click="localValue = !localValue; touched = true" type="button" :id="id" :class="[
        localValue ? 'bg-blue-600' : 'bg-gray-300',
        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring focus:ring-blue-500 border',
      ]">
        <span :class="[
          localValue ? 'translate-x-6' : 'translate-x-1',
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
        ]" />
      </button>
    </div>

    <!-- Searchable Select -->
    <div v-else-if="type === 'searchable-select'" class="relative">
      <input type="text" :placeholder="placeholder || translate('Search...')"
        class="block w-full rounded-md border border-gray-300 px-3 py-3 shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        v-model="search" @input="handleSearch" @focus="dropdownOpen = true" @blur="onBlur" />
      <ul v-if="dropdownOpen && results?.length"
        class="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg text-sm list-none pl-0">
        <li v-for="option in results" :key="option.value" @click="selectOption(option)"
          class="px-4 py-2 hover:bg-blue-100 cursor-pointer">
          {{ option.label }}
        </li>
      </ul>
    </div>

    <!-- Error -->
    <p v-if="computedErrorMessage" class="mt-1 text-sm text-red-600">
      {{ computedErrorMessage }}
    </p>
    <p v-else-if="hint" class="mt-1 text-sm" :class="{ 'text-red-600': error }">
      {{ hint }}
    </p>
  </div>
</template>

<script>
import { translations } from "@/mixins";

export default {
  name: "BaseField",
  mixins: [translations],
  props: {
    id: String,
    name: String,
    value: [String, Number, Boolean, Object],
    type: {
      type: String,
      default: "input", // input | select | toggle | searchable-select
    },
    inputType: {
      type: String,
      default: "text",
    },
    placeholder: String,
    options: {
      type: Array,
      default: () => [],
    },
    searchFunction: Function, // required for type: searchable-select
    hint: String,
    error: Boolean,
    errorMessage: String, // <-- add this line
    disabled: Boolean,
    required: Boolean,
    min: {
      type: [String, Number],
      default: 1,
    },
    max: {
      type: [String, Number],
      default: null,
    },
    valid: {
      type: Boolean,
      default: undefined,
    },
  },
  data() {
    return {
      search: "",
      results: [],
      dropdownOpen: false,
      touched: false, // Track if the field has been interacted with
      localValid: this.valid, // Local validity state
    };
  },
  computed: {
    localValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.touched = true;
      },
    },
    showRequiredError() {
      // Only show if required, touched, and value is empty
      return (
        this.required &&
        this.touched &&
        (this.localValue === null || this.localValue === undefined || this.localValue === '' || (Array.isArray(this.localValue) && this.localValue.length === 0))
      );
    },
    computedErrorMessage() {
      if (!this.touched) return '';
      if (this.showRequiredError) {
        return this.errorMessage || this.translate('This field is required');
      }
      if (this.error && this.errorMessage) {
        return this.errorMessage;
      }
      return '';
    },
  },
  watch: {
    type(val) {
      if (val !== "searchable-select") {
        this.dropdownOpen = false;
        this.search = "";
      }
    },
    valid(newVal) {
      this.localValid = newVal;
    },
    // Keep localValid in sync with value changes
    value() {
      this.updateValidity();
    },
  },
  methods: {
    async handleSearch() {
      this.touched = true;
      if (
        this.searchFunction &&
        this.search?.length > 1 &&
        this.search?.trim() !== ""
      ) {
        await this.searchFunction(this.search, (options) => {
          this.results = options;
        });
      } else {
        this.results = this.options;
      }
    },
    selectOption(option) {
      this.localValue = option.value;
      this.search = option.label;
      this.dropdownOpen = false;
      this.touched = true;
      this.updateValidity();
    },
    handleClickOutside(event) {
      const dropdown = this.$el.querySelector(".relative");
      if (dropdown && !dropdown.contains(event.target)) {
        this.dropdownOpen = false;
      }
    },
    onBlur() {
      this.touched = true;
      this.updateValidity();
    },
    updateValidity() {
      let isValid = true;
      if (this.required) {
        isValid = !(this.localValue === null || this.localValue === undefined || this.localValue === '' || (Array.isArray(this.localValue) && this.localValue.length === 0));
      }
      this.localValid = isValid;
      this.$emit('update:valid', isValid);
      this.$emit('valid', isValid); // for event-based usage
    },
  },
  mounted() {
    if (this.type === "searchable-select") {
      this.results = this.options;
      if (this.value) {
        const selected = this.options.find((opt) => opt.value === this.value);
        if (selected) this.search = selected.label;
      }
      document.addEventListener("click", this.handleClickOutside);
    }
    this.updateValidity();
  },
  beforeUnmount() {
    if (this.type === "searchable-select") {
      document.removeEventListener("click", this.handleClickOutside);
    }
  },
};
</script>
